import { memo, useCallback, useEffect, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Paper from "@mui/material/Paper";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";

import {
  filterBirthdayByScope,
  type BirthdayFilterScope,
} from "../aggregations";
import { formatPatientListLabel } from "../patientLabels";
import type { CrmPatientSummary, FluxZapCommunicationRecord } from "../types";
import type { OnOpenCommunicationsWhatsAppSend } from "../types/sendMessage";
import CommunicationsShortcutPatientListBlock from "./CommunicationsShortcutPatientListBlock";
import {
  communicationsRankFilledSearchFieldSx,
  communicationsRankTableToolbarSx,
} from "./communicationsSubsectionStyles";

export interface CommunicationsBirthdayPatientsDialogProps {
  open: boolean;
  onClose: () => void;
  allPatients: CrmPatientSummary[];
  communications: FluxZapCommunicationRecord[];
  onOpenSendMessage: OnOpenCommunicationsWhatsAppSend;
}

const SCOPE_LABELS: Record<BirthdayFilterScope, string> = {
  day: "Hoje",
  week: "Esta semana",
  month: "Este mês",
};

const actionButtonSx = {
  height: 36,
  px: 2,
  borderRadius: 2,
  textTransform: "none" as const,
};

function normalizeNameSearch(value: string): string {
  return value.trim().toLowerCase();
}

/** Busca apenas pelo nome exibido na lista (usual ou oficial). */
function matchesNameSearch(patient: CrmPatientSummary, query: string): boolean {
  if (!query) return true;
  return formatPatientListLabel(patient).toLowerCase().includes(query);
}

/**
 * Aniversariantes: período (dia/semana/mês) com lista automática + busca por nome.
 */
const CommunicationsBirthdayPatientsDialog = memo(
  ({
    open,
    onClose,
    allPatients,
    communications,
    onOpenSendMessage,
  }: CommunicationsBirthdayPatientsDialogProps) => {
    const [scope, setScope] = useState<BirthdayFilterScope>("day");
    const [refDate] = useState(() => new Date());
    const [nameSearchQuery, setNameSearchQuery] = useState("");

    useEffect(() => {
      if (!open) return;
      setScope("day");
      setNameSearchQuery("");
    }, [open]);

    useEffect(() => {
      setNameSearchQuery("");
    }, [scope]);

    const scopePatients = useMemo(
      () => filterBirthdayByScope(allPatients, scope, refDate),
      [allPatients, refDate, scope],
    );

    const filteredPatients = useMemo(() => {
      const q = normalizeNameSearch(nameSearchQuery);
      if (!q) return scopePatients;
      return scopePatients.filter((p) => matchesNameSearch(p, q));
    }, [nameSearchQuery, scopePatients]);

    const scopeTotalCount = scopePatients.length;
    const visibleCount = filteredPatients.length;
    const hasActiveNameSearch = nameSearchQuery.trim() !== "";

    const resultTitle = useMemo(() => {
      if (hasActiveNameSearch && visibleCount !== scopeTotalCount) {
        return `${visibleCount} de ${scopeTotalCount} ${
          scopeTotalCount === 1 ? "aniversariante" : "aniversariantes"
        }`;
      }
      return `${scopeTotalCount} ${
        scopeTotalCount === 1 ? "aniversariante" : "aniversariantes"
      } — ${SCOPE_LABELS[scope].toLowerCase()}`;
    }, [
      hasActiveNameSearch,
      scope,
      scopeTotalCount,
      visibleCount,
    ]);

    const handleClose = useCallback(() => onClose(), [onClose]);

    return (
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle sx={{ pr: 6 }}>
          <Stack spacing={0.25}>
            <Typography variant="subtitle1" fontWeight={700}>
              Aniversariantes
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Selecione o período e envie mensagens individualmente ou em lote.
            </Typography>
          </Stack>
          <IconButton
            aria-label="Fechar"
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "text.secondary",
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2}>
            <FormControl component="fieldset" variant="standard">
              <FormLabel component="legend" sx={{ mb: 1 }}>
                Período
              </FormLabel>
              <RadioGroup
                row
                value={scope}
                onChange={(e) =>
                  setScope(e.target.value as BirthdayFilterScope)
                }
              >
                {(Object.keys(SCOPE_LABELS) as BirthdayFilterScope[]).map(
                  (key) => (
                    <FormControlLabel
                      key={key}
                      value={key}
                      control={<Radio size="small" />}
                      label={SCOPE_LABELS[key]}
                    />
                  ),
                )}
              </RadioGroup>
            </FormControl>

            <Paper
              variant="outlined"
              sx={{ p: 1.5, borderRadius: 2, bgcolor: "action.hover" }}
            >
              <Stack spacing={1.5}>
                <Typography variant="subtitle2" fontWeight={700}>
                  {resultTitle}
                </Typography>

                <Box
                  sx={{
                    ...communicationsRankTableToolbarSx,
                    mx: -1.5,
                    mt: -0.5,
                    borderRadius: "8px 8px 0 0",
                  }}
                >
                  <TextField
                    fullWidth
                    size="small"
                    variant="filled"
                    label="Buscar por nome"
                    placeholder="Digite o nome do paciente…"
                    value={nameSearchQuery}
                    onChange={(e) => setNameSearchQuery(e.target.value)}
                    sx={communicationsRankFilledSearchFieldSx}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" color="disabled" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                {scopeTotalCount === 0 ? (
                  <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
                    Nenhum aniversariante neste período.
                  </Alert>
                ) : hasActiveNameSearch && visibleCount === 0 ? (
                  <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
                    Nenhum aniversariante corresponde ao nome buscado.
                  </Alert>
                ) : (
                  <CommunicationsShortcutPatientListBlock
                    key={`${scope}-${scopeTotalCount}`}
                    shortcutPatients={filteredPatients}
                    communications={communications}
                    onOpenSendMessage={onOpenSendMessage}
                  />
                )}
              </Stack>
            </Paper>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} sx={actionButtonSx}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    );
  },
);

CommunicationsBirthdayPatientsDialog.displayName =
  "CommunicationsBirthdayPatientsDialog";

export default CommunicationsBirthdayPatientsDialog;
